/**
 * Resolves all parent cookie domains for cross-subdomain cookie sharing.
 */
export function getParentCookieDomains(): string[] {
  const domains: string[] = ['']; // Empty string for host-only (crucial for localhost)
  if (typeof window === 'undefined') return domains;
  const hostname = window.location.hostname || '';
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    return domains;
  }
  domains.push(hostname);
  domains.push('.' + hostname);
  const parts = hostname.split('.');
  while (parts.length > 2) {
    parts.shift();
    const parentDomain = parts.join('.');
    domains.push('.' + parentDomain);
    domains.push(parentDomain);
  }
  return Array.from(new Set(domains));
}

/**
 * Checks whether a cookie with the given name has a specific value.
 */
export function hasCookieValue(name: string, expectedVal: string): boolean {
  if (typeof document === 'undefined') return false;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const parts = cookie.trim().split('=');
    if (parts[0] === name) {
      const val = decodeURIComponent(parts.slice(1).join('=') || '').trim();
      if (val === expectedVal) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Helper to get a cookie value by name (returns first non-empty value).
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const parts = cookie.trim().split('=');
    if (parts[0] === name) {
      const val = decodeURIComponent(parts.slice(1).join('=') || '').trim();
      if (val) {
        return val;
      }
    }
  }
  return null;
}

/**
 * Checks whether contractor has logged out via cross-tab cookie flag or localStorage.
 */
export function isContractorLoggedOut(): boolean {
  if (typeof document === 'undefined') return false;
  // Check cookie first — cookies are shared across ports on localhost and across
  // subdomains in production. This is the MOST reliable cross-origin signal.
  if (hasCookieValue('contractor_logged_out', 'true') || hasCookieValue('contractor_logged_out', '1')) {
    return true;
  }
  // Check localStorage as fallback (only reliable for same-origin tabs)
  try {
    if (
      localStorage.getItem('contractor_logged_out') === 'true' ||
      !!localStorage.getItem('contractor_logged_out_at')
    ) {
      return true;
    }
  } catch (e) {
    //added for lint fail fix
  }
  return false;
}

/**
 * Sets the cross-subdomain/cross-port contractor_logged_out cookie flag.
 */
export function setContractorLoggedOutFlag(): void {
  if (typeof document === 'undefined') return;
  const maxAge = 86400; // 24 hours
  const expires = new Date(Date.now() + maxAge * 1000).toUTCString();
  const domains = getParentCookieDomains();
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const samesiteAttr = isHttps ? '; SameSite=None; Secure' : '; SameSite=Lax';

  for (const domain of domains) {
    const domainAttr = domain ? `; domain=${domain}` : '';
    document.cookie = `contractor_logged_out=true; path=/${domainAttr}; expires=${expires}; max-age=${maxAge}${samesiteAttr}`;
    document.cookie = `contractor_session_active=; path=/${domainAttr}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0${samesiteAttr}`;
    document.cookie = `contractor_id_token=; path=/${domainAttr}; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0${samesiteAttr}`;
  }

  try {
    localStorage.setItem('contractor_logged_out', 'true');
    localStorage.setItem('contractor_logged_out_at', Date.now().toString());
    localStorage.removeItem('contractor_session_active');
    localStorage.removeItem('contractor_auth_token');
  } catch (e) {
    //added for lint fail fix
  }
}

/**
 * Clears the contractor_logged_out cookie flag across all parent domains.
 */
export function clearContractorLoggedOutFlag(): void {
  if (typeof document === 'undefined') return;
  const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const domains = getParentCookieDomains();
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const samesiteAttr = isHttps ? '; SameSite=None; Secure' : '; SameSite=Lax';

  for (const domain of domains) {
    const domainAttr = domain ? `; domain=${domain}` : '';
    document.cookie = `contractor_logged_out=; path=/${domainAttr}; expires=${expired}; max-age=0${samesiteAttr}`;
    document.cookie = `contractor_logged_out=; path=/${domainAttr}; expires=${expired}; max-age=0`;
  }
  document.cookie = `contractor_logged_out=; path=/; expires=${expired}; max-age=0${samesiteAttr}`;
  document.cookie = `contractor_logged_out=; path=/; expires=${expired}; max-age=0`;
  document.cookie = `contractor_logged_out=; expires=${expired}; max-age=0`;

  try {
    localStorage.removeItem('contractor_logged_out');
    localStorage.removeItem('contractor_logged_out_at');
    localStorage.setItem('contractor_session_active', 'true');
  } catch (e) {
    //added for lint fail fix
  }
}

/**
 * Searches cookies for contractor id_token.
 */
export function getContractorTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  if (isContractorLoggedOut()) return null;
  const hostname = window.location.hostname.replace(/\./g, '');
  const legacyHostname = window.location.hostname.replace('.', '');
  const possibleNames = [
    'contractor_id_token',
    `${hostname}id_token`,
    `${legacyHostname}id_token`,
    'id_token',
    'contractor_auth_token',
  ];
  for (const name of possibleNames) {
    const val = getCookie(name);
    if (val && val.startsWith('eyJ')) {
      return val;
    }
  }
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cName, cVal] = cookie.trim().split('=');
    if (cName && cName.endsWith('id_token') && cVal && cVal.startsWith('eyJ')) {
      return decodeURIComponent(cVal);
    }
  }
  return null;
}

/**
 * Checks whether current hostname is a contractor portal host where cookies are set.
 */
export function isContractorHost(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname || '';
  return hostname.startsWith('portal.') || hostname.includes('partnerportal');
}

/**
 * Checks whether current hostname is local development.
 */
export function isLocalHost(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname || '';
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Checks whether the current session is an active contractor session.
 */
export function isContractorSession(): boolean {
  if (typeof sessionStorage !== 'undefined') {
    if (
      sessionStorage.getItem('is_contractor_session') === 'true' ||
      sessionStorage.getItem('apply_user_type') === 'contractor'
    ) {
      return true;
    }
  }
  if (typeof window !== 'undefined') {
    if (isContractorHost() && !isContractorLoggedOut() && !!getContractorTokenFromCookie()) {
      return true;
    }
  }
  return false;
}

/**
 * Retrieves the stored auth token.
 */
export function getStoredAuthToken(): string | null {
  if (typeof sessionStorage !== 'undefined') {
    if (isContractorSession() && isContractorLoggedOut()) {
      clearAuthSession();
      return null;
    }
    const token = sessionStorage.getItem('apply_auth_token');
    if (token) return token;

    if (isContractorSession()) {
      const cookieToken = getContractorTokenFromCookie();
      if (cookieToken) {
        sessionStorage.setItem('apply_auth_token', cookieToken);
        sessionStorage.setItem('is_contractor_session', 'true');
        sessionStorage.setItem('apply_user_type', 'contractor');
        return cookieToken;
      }
      if (isContractorHost()) {
        handleContractorLogout('logged_out_in_other_tab');
        return null;
      }
    }
  }
  return null;
}

/**
 * Retrieves stored userType.
 */
export function getStoredUserType(): string | null {
  if (typeof sessionStorage !== 'undefined') {
    if (isContractorSession() && isContractorLoggedOut()) {
      clearAuthSession();
      return null;
    }
    const type = sessionStorage.getItem('apply_user_type');
    if (type) return type;

    if (isContractorSession()) {
      return 'contractor';
    }
  }
  return null;
}

/**
 * Persists auth session details to sessionStorage.
 */
export function saveAuthSession(authToken?: string | null, userType?: string | null): void {
  if (typeof sessionStorage !== 'undefined') {
    if (authToken) {
      sessionStorage.setItem('apply_auth_token', authToken);
    } else {
      sessionStorage.removeItem('apply_auth_token');
    }
    if (userType === 'contractor') {
      sessionStorage.setItem('is_contractor_session', 'true');
      sessionStorage.setItem('apply_user_type', 'contractor');
      clearContractorLoggedOutFlag();
    } else {
      sessionStorage.removeItem('is_contractor_session');
      if (userType) {
        sessionStorage.setItem('apply_user_type', userType);
      } else {
        sessionStorage.removeItem('apply_user_type');
      }
    }
  }
}

/**
 * Clears auth session details.
 */
export function clearAuthSession(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('apply_auth_token');
    sessionStorage.removeItem('apply_user_type');
    sessionStorage.removeItem('is_contractor_session');
  }
}

/**
 * Resolves the appropriate contractor login URL based on environment.
 */
export function getContractorLoginUrl(returnUrl?: string): string {
  if (typeof window === 'undefined') return '/login';
  const hostname = window.location.hostname;
  const targetReturn = returnUrl;
  const redirectParam = targetReturn ? `?redirect=${encodeURIComponent(targetReturn)}` : '';

  if (hostname.includes('staging')) {
    return `https://portal.staging.ygreneworks.com/#/login${redirectParam}`;
  }
  if (hostname.includes('demo')) {
    return `https://portal.demo.ygreneworks.com/#/login${redirectParam}`;
  }
  if (
    hostname.includes('ygreneworks.com') &&
    !hostname.includes('acceptance') &&
    !hostname.includes('staging') &&
    !hostname.includes('demo')
  ) {
    return `https://portal.ygreneworks.com/v1/index.html${redirectParam}`;
  }
  // Acceptance environment and local development (localhost / 127.0.0.1)
  return `https://portal.acceptance.ygreneworks.com/#/login${redirectParam}`;
}

let isContractorLoggingOut = false;

/**
 * Handles contractor logout and redirection ONLY when a contractor is unauthorized or session expired.
 */
export function handleContractorLogout(reason = 'unauthorized'): void {
  const isContractor =
    isContractorSession() ||
    getStoredUserType() === 'contractor' ||
    (typeof sessionStorage !== 'undefined' &&
      (sessionStorage.getItem('apply_user_type') === 'contractor' ||
        sessionStorage.getItem('is_contractor_session') === 'true'));

  if (!isContractor) {
    return;
  }

  if (isContractorLoggingOut) {
    return;
  }
  isContractorLoggingOut = true;

  clearAuthSession();
  setContractorLoggedOutFlag();

  if (typeof window !== 'undefined') {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const authChannel = new BroadcastChannel('contractor_auth_channel');
        authChannel.postMessage({ type: 'LOGOUT', reason, source: 'apply' });
        authChannel.close();
        const sessionChannel = new BroadcastChannel('contractor_session_channel');
        sessionChannel.postMessage({ type: 'LOGOUT', reason, source: 'apply' });
        sessionChannel.close();
      }
    } catch (e) {
    //added for lint fail fix
    }

    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({ type: 'LOGOUT', reason, source: 'apply' }, '*');
      } catch (e) {
    //added for lint fail fix
      }
      return;
    }
    try {
      const loginUrl = getContractorLoginUrl();
      if (window.top) {
        window.top.location.href = loginUrl;
      } else {
        window.location.href = loginUrl;
      }
    } catch (e) {
    //added for lint fail fix
    }
  }
}

export function setupContractorCrossTabSync(): void {
  if (typeof window === 'undefined') return;

  const checkContractorSession = () => {
    const isContractor =
      isContractorSession() ||
      getStoredUserType() === 'contractor' ||
      (typeof sessionStorage !== 'undefined' &&
        (sessionStorage.getItem('apply_user_type') === 'contractor' ||
          sessionStorage.getItem('is_contractor_session') === 'true'));

    if (isContractor) {
      if (isContractorLoggedOut()) {
        handleContractorLogout('logged_out_in_other_tab');
        return;
      }
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage({ type: 'CHECK_AUTH', request: 'check_auth' }, '*');
        } catch (e) {
          // Ignore cross-origin error
        }
      }
    }
  };

  window.addEventListener('focus', checkContractorSession);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkContractorSession();
      }
    });
    document.addEventListener('click', () => {
      checkContractorSession();
    });
  }

  setInterval(checkContractorSession, 1000);

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const crossTabChannel = new BroadcastChannel('contractor_auth_channel');
      crossTabChannel.onmessage = (event) => {
        if (event.data && event.data.type === 'LOGOUT') {
          if (event.data.source === 'apply') return;
          const isContractor =
            isContractorSession() ||
            getStoredUserType() === 'contractor' ||
            (typeof sessionStorage !== 'undefined' &&
              (sessionStorage.getItem('apply_user_type') === 'contractor' ||
                sessionStorage.getItem('is_contractor_session') === 'true'));
          if (isContractor) {
            handleContractorLogout('logged_out_in_other_tab');
          }
        } else if (event.data && event.data.type === 'LOGIN') {
          clearContractorLoggedOutFlag();
        }
      };
    }
  } catch (e) {
    //added for lint fail fix
  }

  window.addEventListener('storage', (event) => {
    if (
      event.key === 'contractor_logged_out' ||
      event.key === 'contractor_logged_out_at' ||
      event.key === 'contractor_id_token' ||
      event.key === 'id_token' ||
      event.key === 'contractor_auth_token' ||
      event.key === 'apply_auth_token' ||
      event.key === null
    ) {
      checkContractorSession();
    }
  });

  window.addEventListener('message', (event) => {
    if (!event.data) return;
    if (event.data.type === 'LOGOUT' || event.data.type === 'SESSION_EXPIRED') {
      if (event.data.source === 'apply') return;
      const isContractor =
        isContractorSession() ||
        getStoredUserType() === 'contractor' ||
        (typeof sessionStorage !== 'undefined' &&
          (sessionStorage.getItem('apply_user_type') === 'contractor' ||
            sessionStorage.getItem('is_contractor_session') === 'true'));
      if (isContractor) {
        handleContractorLogout('logged_out_in_other_tab');
      }
    }
  });
}

if (typeof window !== 'undefined') {
  setupContractorCrossTabSync();
}
